import { Analytics } from '@cobliteam/analytics';
import { ConditionCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import type { TriggerEvent } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/events';
import { Location } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Button } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { getTime, startOfDay } from '@cobliteam/utils/date';
import queryString from 'query-string';
import React from 'react';
import { Link } from 'react-router-dom';

const getTriggerEventIcon = (triggerEvent: TriggerEvent) => {
  const {
    meta: { conditionCategory },
  } = triggerEvent;

  switch (conditionCategory) {
    case ConditionCategory.GEOFENCE_IN:
    case ConditionCategory.GEOFENCE_OUT:
    case ConditionCategory.GEOFENCE_IN_OUT:
      return <Icon name='map-pinned' />;

    case ConditionCategory.IDLE_ENGINE_3_MINUTES:
    case ConditionCategory.IDLE_ENGINE_5_MINUTES:
    case ConditionCategory.IDLE_ENGINE_10_MINUTES:
    case ConditionCategory.IDLE_ENGINE_15_MINUTES:
    case ConditionCategory.IDLE_ENGINE_30_MINUTES:
      return <Icon name='clock' />;

    case ConditionCategory.DEVICE_CONNECTION_CONNECTED:
    case ConditionCategory.DEVICE_CONNECTION_DISCONNECTED:
    case ConditionCategory.DEVICE_CONNECTION_CONNECTED_DISCONNECTED:
      return <Icon name='unplug' />;

    case ConditionCategory.VEHICLE_SPEED_LIMIT_30_SECONDS:
    case ConditionCategory.VEHICLE_SPEED_LIMIT_1_MINUTE:
    case ConditionCategory.VEHICLE_SPEED_LIMIT_5_MINUTES:
    case ConditionCategory.VEHICLE_SPEED_LIMIT_10_MINUTES:
      return <Icon name='zap' />;

    case ConditionCategory.ROAD_SPEED_LIMIT_30_SECONDS:
    case ConditionCategory.ROAD_SPEED_LIMIT_1_MINUTE:
    case ConditionCategory.ROAD_SPEED_LIMIT_5_MINUTES:
    case ConditionCategory.ROAD_SPEED_LIMIT_10_MINUTES:
      return <Icon name='gauge' />;

    case ConditionCategory.DRIVER_IDENTIFICATION_WITHOUT_DRIVER:
      return <Icon name='square-user-round' />;

    case ConditionCategory.CHECKLIST_UNFILLED:
      return <Icon name='list-checks' />;

    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED_OR_UNOBSTRUCTED:
    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED:
    case ConditionCategory.DRIVER_CAMERA_UNOBSTRUCTED:
      return <Icon name='video-off' color='danger' />;

    default:
      return null;
  }
};

const getTriggerPrimaryButton = (triggerEvent: TriggerEvent, eventType: string) => {
  const { t } = useTranslation('app-header');
  const { vehicleId, eventTime, id: eventId } = triggerEvent;

  if (!vehicleId) return;
  return (
    <Link
      to={{
        pathname: Location.TRACKING,
        search: `?${queryString.stringify({
          vehicle_id: vehicleId,
          date: getTime(startOfDay(new Date(eventTime))),
          event_type: eventType,
          event_id: eventId,
          event_time: getTime(startOfDay(new Date(eventTime))),
        })}`,
      }}
      target='_blank'
      rel='noopener noreferrer'
      className='ml-auto flex w-fit'
      onClick={() => Analytics.track('NavBar - Notification - ViewTrackingHistory')}
    >
      <Button variant='ghost' color='primary' size='sm' startIcon={<Icon name='route' />}>
        {t('notifications.viewTracking')}
      </Button>
    </Link>
  );
};

const getTriggerEventInfo = (triggerEvent: TriggerEvent) => {
  const { t } = useTranslation('app-header');
  const { meta } = triggerEvent;
  const triggerIcon = getTriggerEventIcon(triggerEvent);
  const icon = triggerIcon
    ? React.cloneElement(triggerIcon, {
        className: 'size-4 bg-gray-lightest rounded-full p-1 box-content overflow-visible',
      })
    : null;

  switch (meta.conditionCategory) {
    // Geofence
    case ConditionCategory.GEOFENCE_IN:
      return {
        icon,
        rule: t('rules.geofence'),
        condition: t('conditions.entry'),
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };
    case ConditionCategory.GEOFENCE_OUT:
      return {
        icon,
        rule: t('rules.geofence'),
        condition: t('conditions.exit'),
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };
    case ConditionCategory.GEOFENCE_IN_OUT:
      return {
        icon,
        rule: t('rules.geofence'),
        condition: t('conditions.entryOrExit'),
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };
    case ConditionCategory.GEOFENCE_SPEED:
      return {
        icon,
        rule: t('rules.geofence'),
        condition: t('conditions.speed'),
        eventType: 'geofence',
        path: Location.FLEET_POLICY_GEOFENCE,
      };

    // Idle engine
    case ConditionCategory.IDLE_ENGINE_3_MINUTES:
      return {
        icon,
        rule: t('rules.idleEngine'),
        condition: t('conditions.from3Minutes'),
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_5_MINUTES:
      return {
        icon,
        rule: t('rules.idleEngine'),
        condition: t('conditions.from5Minutes'),
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_10_MINUTES:
      return {
        icon,
        rule: t('rules.idleEngine'),
        condition: t('conditions.from10Minutes'),
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_15_MINUTES:
      return {
        icon,
        rule: t('rules.idleEngine'),
        condition: t('conditions.from15Minutes'),
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };
    case ConditionCategory.IDLE_ENGINE_30_MINUTES:
      return {
        icon,
        rule: t('rules.idleEngine'),
        condition: t('conditions.from30Minutes'),
        eventType: 'stop',
        path: Location.FLEET_POLICY_IDLE_ENGINE,
      };

    // Device connection
    case ConditionCategory.DEVICE_CONNECTION_CONNECTED:
      return {
        icon,
        rule: t('rules.deviceConnection'),
        condition: t('conditions.connected'),
        eventType: 'incident',
        path: Location.FLEET_POLICY_DEVICE_CONNECTION,
      };
    case ConditionCategory.DEVICE_CONNECTION_DISCONNECTED:
      return {
        icon,
        rule: t('rules.deviceConnection'),
        condition: t('conditions.disconnected'),
        eventType: 'incident',
        path: Location.FLEET_POLICY_DEVICE_CONNECTION,
      };
    case ConditionCategory.DEVICE_CONNECTION_CONNECTED_DISCONNECTED:
      return {
        icon,
        rule: t('rules.deviceConnection'),
        condition: t('conditions.connectedAndDisconnected'),
        eventType: 'incident',
        path: Location.FLEET_POLICY_DEVICE_CONNECTION,
      };

    // Vehicle speed limit
    case ConditionCategory.VEHICLE_SPEED_LIMIT_30_SECONDS:
      return {
        icon,
        rule: t('rules.vehicleSpeed'),
        condition: t('conditions.from30SecondsDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT,
      };
    case ConditionCategory.VEHICLE_SPEED_LIMIT_1_MINUTE:
      return {
        icon,
        rule: t('rules.vehicleSpeed'),
        condition: t('conditions.from1MinuteDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT,
      };
    case ConditionCategory.VEHICLE_SPEED_LIMIT_5_MINUTES:
      return {
        icon,
        rule: t('rules.vehicleSpeed'),
        condition: t('conditions.from5MinutesDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT,
      };
    case ConditionCategory.VEHICLE_SPEED_LIMIT_10_MINUTES:
      return {
        icon,
        rule: t('rules.vehicleSpeed'),
        condition: t('conditions.from10MinutesDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT,
      };

    // Road speed limit
    case ConditionCategory.ROAD_SPEED_LIMIT_30_SECONDS:
      return {
        icon,
        rule: t('rules.roadSpeed'),
        condition: t('conditions.from30SecondsDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_ROAD_SPEED_LIMIT,
      };
    case ConditionCategory.ROAD_SPEED_LIMIT_1_MINUTE:
      return {
        icon,
        rule: t('rules.roadSpeed'),
        condition: t('conditions.from1MinuteDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_ROAD_SPEED_LIMIT,
      };
    case ConditionCategory.ROAD_SPEED_LIMIT_5_MINUTES:
      return {
        icon,
        rule: t('rules.roadSpeed'),
        condition: t('conditions.from5MinutesDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_ROAD_SPEED_LIMIT,
      };
    case ConditionCategory.ROAD_SPEED_LIMIT_10_MINUTES:
      return {
        icon,
        rule: t('rules.roadSpeed'),
        condition: t('conditions.from10MinutesDriven'),
        eventType: 'speed',
        path: Location.FLEET_POLICY_ROAD_SPEED_LIMIT,
      };

    // Checklist
    case ConditionCategory.CHECKLIST_UNFILLED:
      return {
        icon,
        rule: t('rules.checklist'),
        condition: t('conditions.notFilled'),
        eventType: 'checklist',
        path: Location.FLEET_POLICY_CHECKLIST,
      };

    // Driver identification
    case ConditionCategory.DRIVER_IDENTIFICATION_WITHOUT_DRIVER:
      return {
        icon,
        rule: t('rules.driverIdentification'),
        condition: t('conditions.driverNotIdentified'),
        eventType: 'driver-identification',
        path: Location.FLEET_POLICY_DRIVER_IDENTIFICATION,
      };

    // Cameras Health
    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED_OR_UNOBSTRUCTED:
      return {
        icon,
        rule: t('rules.cameraHealth'),
        condition: t('conditions.obstructedOrUnobstructed'),
        eventType: 'cameras-health',
        path: Location.FLEET_POLICY_CAMERAS_HEALTH,
      };

    case ConditionCategory.DRIVER_CAMERA_OBSTRUCTED:
      return {
        icon,
        rule: t('rules.cameraHealth'),
        condition: t('conditions.obstructed'),
        eventType: 'cameras-health',
        path: Location.FLEET_POLICY_CAMERAS_HEALTH,
      };

    case ConditionCategory.DRIVER_CAMERA_UNOBSTRUCTED:
      return {
        icon,
        rule: t('rules.cameraHealth'),
        condition: t('conditions.unobstructed'),
        eventType: 'cameras-health',
        path: Location.FLEET_POLICY_CAMERAS_HEALTH,
      };

    default:
      return null;
  }
};

export { getTriggerEventInfo, getTriggerPrimaryButton };
